/* ═══════════════════════════════════════════════════════════════
   ModuleCategorySelect — single-select picker for a module's CATEGORY
   (migration 056). Every category, labelled by its full path, plus
   "No category". Type-to-create: typing a path that doesn't exist yet
   ("Equipment > Division 27 > Speakers > QSC") offers to create every
   missing level in order and assigns the module to the new leaf.

   Composed from the kit `SearchableSelect` — never a second dropdown.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { SearchableSelect } from './SearchableSelect';
import { categoryCreateLabel } from '../lib/moduleBrowserColumns';
import {
  categoryPath,
  categoryPathLabel,
  parseCategoryPath,
  type ModuleCategory,
} from '../../shared/module-categories';
import { CategoriesService } from '../services/categoriesService';

const NO_CATEGORY = '__none__';

export interface ModuleCategorySelectProps {
  value: string | null;
  onChange: (categoryId: string | null) => void;
  categories: ModuleCategory[];
  /** `modules.create` — may typing a new path create it? Off ⇒ the
   *  create row never appears; the picker is a plain assign-only select. */
  canCreate: boolean;
  userName?: string;
  disabled?: boolean;
  placeholder?: string;
  ariaLabel?: string;
  describedBy?: string;
  onError?: (message: string) => void;
  /** Renders in flow (no trigger/portal) — the bulk "move to" picker. Rows
   *  show the category's OWN name, indented by depth, with `count` at the
   *  right — a hierarchy, not a flat breadcrumb — still sorted by full path
   *  so parent/child stay adjacent. Omit (every existing caller) and this
   *  is the plain dropdown with full-path labels, byte-for-byte unchanged. */
  inline?: boolean;
  /** Module count per category id (`moduleCountByCategory`) — shown
   *  right-aligned when `inline`. Omit and no count renders. */
  countByCategory?: Record<string, number>;
  /** Shown under the search input in `inline` mode when `canCreate` — see
   *  `SearchableSelect`'s `createHint`. */
  createHint?: string;
}

export function ModuleCategorySelect({
  value,
  onChange,
  categories,
  canCreate,
  userName,
  disabled,
  placeholder,
  ariaLabel,
  describedBy,
  onError,
  inline,
  countByCategory,
  createHint,
}: ModuleCategorySelectProps) {
  const [busy, setBusy] = React.useState(false);

  const options = React.useMemo(() => {
    if (inline) {
      // Sort by the FULL path string — a parent and its descendants share
      // that string as a prefix, so this reads as a tree walk even though
      // each row's own label is just its own name.
      const cats = (categories || [])
        .map((c) => ({
          value: c.id,
          label: c.name,
          sortKey: categoryPathLabel(c.id, categories),
          indent: categoryPath(c.id, categories).length - 1,
          count: countByCategory?.[c.id] ?? 0,
        }))
        .sort((a, b) => a.sortKey.localeCompare(b.sortKey));
      return [
        { value: NO_CATEGORY, label: 'No category' },
        ...cats.map(({ sortKey: _sortKey, ...o }) => o),
      ];
    }
    const cats = (categories || [])
      .map((c) => ({ value: c.id, label: categoryPathLabel(c.id, categories) }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return [{ value: NO_CATEGORY, label: 'No category' }, ...cats];
  }, [categories, inline, countByCategory]);

  return (
    <SearchableSelect
      value={value ?? NO_CATEGORY}
      onChange={(v) => onChange(v === NO_CATEGORY ? null : v)}
      options={options}
      placeholder={busy ? 'Creating…' : placeholder || 'No category'}
      searchPlaceholder="Search, or type a path to create one"
      clearable={false}
      disabled={disabled || busy}
      ariaLabel={ariaLabel || 'Category'}
      describedBy={describedBy}
      inline={inline}
      createHint={createHint}
      // Dropdown mode (ModuleBuilder, SaveAsModuleDialog, the single-row
      // "Category and tags…" dialog) keeps SearchableSelect's own default
      // ("No matches") byte-for-byte — only `inline` (the bulk move-to
      // picker) gets the more specific wording.
      {...(inline ? { noResultsText: (q: string) => `No categories match "${q}"` } : {})}
      creatable={canCreate}
      creatableLabel={(q) => categoryCreateLabel(q) || `Create "${q}"`}
      onCreate={async (typed) => {
        const names = parseCategoryPath(typed);
        if (!names.length) return;
        setBusy(true);
        try {
          const id = await CategoriesService.resolveOrCreatePath(names, categories, userName);
          onChange(id);
        } catch (err) {
          onError?.(
            err instanceof Error && err.message ? err.message : 'That category could not be created'
          );
        } finally {
          setBusy(false);
        }
      }}
    />
  );
}

export default ModuleCategorySelect;
